import { TrashIcon } from '../../../assets/icons';
import { Field, fieldCls } from './formFields';

type SetField = (field: string, value: unknown) => void;

/**
 * The Mobile (repeatable) / Landline+Ext / WhatsApp row — a single responsive 12-col
 * grid, bottom-aligned so the inputs line up despite differing label heights.
 * Any mobile number can be designated the WhatsApp number via its "WhatsApp" toggle
 * (`whatsappIndex`); -1 means the WhatsApp number is entered manually.
 */
export default function PhoneRow({
  contact,
  landline,
  landlineExt,
  whatsapp,
  whatsappIndex,
  contactError,
  setFieldValue,
  showLandline = true,
}: {
  contact: string[];
  landline: string;
  landlineExt: string;
  whatsapp: string;
  whatsappIndex: number;
  contactError?: string;
  setFieldValue: SetField;
  /** Hide the Landline & Extension column (e.g. Important Person form). */
  showLandline?: boolean;
}) {
  const linked = whatsappIndex >= 0;
  const whatsappValue = linked ? (contact[whatsappIndex] ?? '') : whatsapp;
  // Mobile + WhatsApp split the row evenly when the landline column is hidden.
  const colSpan = showLandline ? 'md:col-span-4' : 'md:col-span-6';

  const removeContact = (i: number) => {
    const next = contact.filter((_, idx) => idx !== i);
    setFieldValue('contact', next.length ? next : ['']);
    // Keep the WhatsApp link pointing at the right number.
    if (whatsappIndex === i) setFieldValue('whatsappIndex', -1);
    else if (whatsappIndex > i) setFieldValue('whatsappIndex', whatsappIndex - 1);
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-start">
      {/* Mobile — repeatable */}
      <div className={`flex min-w-0 flex-col gap-1.5 ${colSpan}`}>
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium leading-4 text-[#475569]">
            Mobile Number<span className="text-[#ef4444]"> *</span>
          </label>
          <button
            type="button"
            onClick={() => setFieldValue('contact', [...contact, ''])}
            className="shrink-0 text-[11px] font-medium text-brand hover:underline"
          >
            + Add another number
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {contact.map((num, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className={[
                  'flex flex-1 overflow-hidden rounded-lg border transition-colors focus-within:ring-2',
                  contactError
                    ? 'border-red-400 focus-within:ring-red-100'
                    : 'border-[#e2e8f0] focus-within:border-brand focus-within:ring-brand/15',
                ].join(' ')}
              >
                <span className="flex items-center border-r border-[#e2e8f0] bg-[#f8fafc] px-3.5 text-sm font-medium text-[#64748b]">
                  +91
                </span>
                <input
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="98765 43210"
                  value={num}
                  onChange={(e) =>
                    setFieldValue(
                      'contact',
                      contact.map((x, idx) =>
                        idx === i ? e.target.value.replace(/\D/g, '').slice(0, 10) : x,
                      ),
                    )
                  }
                  className="min-w-0 flex-1 !rounded-none !border-0 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-[#94a3b8] focus:!shadow-none focus:outline-none"
                />
              </div>
              {/* Designate this number as the WhatsApp number. */}
              <button
                type="button"
                aria-pressed={whatsappIndex === i}
                title="Use this number for WhatsApp"
                onClick={() => setFieldValue('whatsappIndex', whatsappIndex === i ? -1 : i)}
                className={[
                  'shrink-0 rounded-lg border px-2.5 py-2 text-[10px] font-semibold transition-colors',
                  whatsappIndex === i
                    ? 'border-[#10b981] bg-[#ecfdf5] text-[#059669]'
                    : 'border-[#e2e8f0] text-[#64748b] hover:bg-[#f8fafc]',
                ].join(' ')}
              >
                WhatsApp
              </button>
              {contact.length > 1 && (
                <button
                  type="button"
                  aria-label="Remove number"
                  onClick={() => removeContact(i)}
                  className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#ef4444] transition-colors hover:bg-red-50"
                >
                  <TrashIcon className="size-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {contactError && <span className="text-xs font-medium text-[#ba1a1a]">{contactError}</span>}
      </div>

      {/* Landline & Extension */}
      {showLandline && (
        <div className="md:col-span-4">
          <Field label="Landline & Extension">
            <div className="grid grid-cols-3 gap-2">
              <input
                placeholder="e.g. 011-23456789"
                value={landline}
                onChange={(e) => setFieldValue('landline', e.target.value)}
                className={`${fieldCls()} col-span-2`}
              />
              <input
                placeholder="Ext. 104"
                value={landlineExt}
                onChange={(e) => setFieldValue('landlineExt', e.target.value)}
                className={fieldCls()}
              />
            </div>
          </Field>
        </div>
      )}

      {/* WhatsApp */}
      <div className={`flex min-w-0 flex-col gap-1.5 ${colSpan}`}>
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium leading-4 text-[#475569]">WhatsApp Number</label>
          {linked ? (
            <button
              type="button"
              onClick={() => setFieldValue('whatsappIndex', -1)}
              className="text-[10px] font-medium text-brand hover:underline"
            >
              Enter manually
            </button>
          ) : (
            <span className="text-[10px] text-[#94a3b8]">
              or tap “WhatsApp” on a number
            </span>
          )}
        </div>
        <div className="flex overflow-hidden rounded-lg border border-[#e2e8f0] focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/15">
          <span className="flex items-center border-r border-[#e2e8f0] bg-[#f8fafc] px-3.5 text-sm font-medium text-[#64748b]">
            +91
          </span>
          <input
            inputMode="numeric"
            maxLength={10}
            placeholder="98765 43210"
            value={whatsappValue}
            disabled={linked}
            onChange={(e) => setFieldValue('whatsapp', e.target.value.replace(/\D/g, '').slice(0, 10))}
            className="min-w-0 flex-1 !rounded-none !border-0 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-[#94a3b8] focus:!shadow-none focus:outline-none disabled:bg-[#f8fafc]"
          />
        </div>
      </div>
    </div>
  );
}
